import { KeyboardArrowDown, Timer } from '@material-ui/icons';
import { addDays, format, isWithinInterval, parse } from 'date-fns';
import { Huddle, Payout } from 'lib/generated/graphql';
import { DateTime } from 'luxon';
import { useState } from 'react';
import useAuth from 'src/shared-components/Auth';
import PayoutDatesRow from './components/PayIntervalRow';

export type PayoutDates = {
  payout: Payout;
  firstPayoutDate: string;
  secondPayoutDate: string;
};

const BuilderPayments = ({ huddle }: { huddle: Huddle }) => {
  const [pastPaymentsOpen, setPastPaymentsOpen] = useState(false);
  const user = useAuth().user;

  const togglePastPayments = () => {
    setPastPaymentsOpen(!pastPaymentsOpen);
  };

  const getPayoutDates = () => {
    const builderPayouts = [];
    let runningDate = huddle.proposal.paySchedule.payIntervals[0].endDate;
    let upcomingPayoutDate = null;

    const today = new Date();
    const fifteenDaysFromNow = addDays(today, 15);

    huddle.proposal.paySchedule.payIntervals.forEach(interval => {
      interval.payouts.forEach(payout => {
        if (payout.builderId === user?.id) {
          let newDate = DateTime.fromISO(runningDate);
          while (newDate.day !== 1 && newDate.day !== 15) {
            newDate = newDate.plus({ days: 1 });
          }
          const firstPayout = newDate.toFormat('L/d/yy');
          newDate = newDate.plus({ days: 1 });
          while (newDate.day !== 1 && newDate.day !== 15) {
            newDate = newDate.plus({ days: 1 });
          }
          const secondPayout = newDate.toFormat('L/d/yy');

          const firstPayoutDate = parse(firstPayout, 'L/d/yy', new Date());
          const secondPayoutDate = parse(secondPayout, 'L/d/yy', new Date());

          // Check if first payout date is within 15 days and is the closest date so far
          if (
            isWithinInterval(firstPayoutDate, { start: today, end: fifteenDaysFromNow }) &&
            (!upcomingPayoutDate || firstPayoutDate < upcomingPayoutDate)
          ) {
            upcomingPayoutDate = firstPayoutDate;
          }

          // Check if second payout date is within 15 days and is the closest date so far
          if (
            isWithinInterval(secondPayoutDate, { start: today, end: fifteenDaysFromNow }) &&
            (!upcomingPayoutDate || secondPayoutDate < upcomingPayoutDate)
          ) {
            upcomingPayoutDate = secondPayoutDate;
          }

          runningDate = newDate.toISO();
          builderPayouts.push({ 
            payout: payout, 
            firstPayoutDate: firstPayout, 
            secondPayoutDate: secondPayout
          });
        }
      });
    });

    // If there's an upcoming payout date, format it
    const nextPayoutDate = upcomingPayoutDate ? format(upcomingPayoutDate, 'MMMM dd yyyy') : null;

    // Return the builder payouts and the next payout date
    return { builderPayouts, nextPayoutDate };
  };

  return (
    <>
      <div className="flex flex-row gap-x-5">
        <div onClick={togglePastPayments} className=" grid content-center text-sm font-bold underline cursor-pointer">
          <div className="flex flex-row">
            <div className="grid content-center">
              <div className="whitespace-nowrap text-lg font-medium">Payment Schedule</div>
            </div>

            <div className="basis-1/3">
              <KeyboardArrowDown
                className={`transform transition-all ease-in-out duration-300 ${
                  pastPaymentsOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </div>
          </div>
        </div>
        <div className="w-full justify-right">
          <div className={`text-md bg-[#DEEBE9] rounded-full cursor-default`}>
            {getPayoutDates().nextPayoutDate && (
              <div className="flex flex-auto content-center">
                <div className="grid content-center">
                  <div className="bg-[#ABBBB9] rounded-full border font-xs flex items-center whitespace-nowrap px-3 py-3 gap-2">
                    <Timer />
                  </div>
                </div>
                <div className="grid content-center ml-5">
                  <span className="text-md text-lg">Next payment: {getPayoutDates().nextPayoutDate}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={`transform ease-in-out duration-300 ${
          pastPaymentsOpen ? 'max-h-[1000px] opacity-100 visible' : 'max-h-0 opacity-0 invisible'
        }`}
      >
        <div className="">
          <div className="flex flex-row justify-between ml-auto mb-3 mt-2">
            <div className="text-md font-bold"> Hours Worked: </div>
            <div className="text-md font-bold"> Hourly Rate: </div>
            <div className="text-md font-bold"> Payment Amount: </div>
            <div className="text-md font-bold"> Payment Date:</div>
          </div>
          {getPayoutDates().builderPayouts.map(payoutDate => (
            <PayoutDatesRow key={payoutDate.payout.id} payoutDates={payoutDate} />
          ))}
        </div>
      </div>
    </>
  );
};

export default BuilderPayments;
